<template>
  <div class="podcast-player-wrapper">
    <button class="close-btn" @click="$emit('close')" aria-label="Fechar">
      <svg viewBox="0 0 24 24" width="24" height="24">
        <line x1="6" y1="6" x2="18" y2="18" stroke="#181818" stroke-width="2" stroke-linecap="round"/>
        <line x1="6" y1="18" x2="18" y2="6" stroke="#181818" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
    <div class="playlist-title">{{ currentTitle }}</div>
    <div class="player-row">
      <button
        @click="playPrev"
        :disabled="!hasPrev"
        class="playlist-btn"
        aria-label="Anterior"
      >
        <svg viewBox="0 0 24 24">
          <rect x="3" y="4" width="2" height="16" rx="1" />
          <polygon points="21,19 9,12 21,5" />
        </svg>
      </button>

      <vue-plyr ref="plyr" :options="playerOptions">
        <audio @play="onPlay" @canplay="onCanPlay">
          <source :src="src" type="audio/mp3" />
          Seu navegador não suporta áudio.
        </audio>
      </vue-plyr>

      <button
        @click="playNext"
        :disabled="!hasNext"
        class="playlist-btn"
        aria-label="Próximo"
      >
        <svg viewBox="0 0 24 24">
          <rect x="19" y="4" width="2" height="16" rx="1" />
          <polygon points="3,5 15,12 3,19" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "PodcastPlaylist",
  props: {
    src: { type: String, required: true },
    playlist: { type: Array, default: () => [] },
    currentIndex: { type: Number, default: 0 },
    forcePlay: { type: Boolean, default: false },
  },
  data() {
    return {
      player: null,
      playerOptions: {
        speed: {
          selected: 1,
          options: [0.5, 1, 1.5, 1.75, 2, 2.5],
        },
        controls: [
          "play",
          "progress",
          "current-time",
          "mute",
          "volume",
          "settings",
        ],
      },
    };
  },
  computed: {
    hasPrev() {
      return this.currentIndex > 0;
    },
    hasNext() {
      return this.currentIndex < this.playlist.length - 1;
    },
    currentTitle() {
      return this.playlist[this.currentIndex]?.title || "";
    },
  },
  watch: {
    src: {
      handler(newSrc) {
        if (this.player && newSrc) {
          this.player.pause();
          this.player.source = {
            type: "audio",
            sources: [{ src: newSrc, type: "audio/mp3" }],
          };
          this.$nextTick(() => {
            setTimeout(() => {
              this.player?.play().catch(() => {});
            }, 100);
          });
        }
      },
      immediate: true,
    },
    forcePlay(val) {
      if (val && this.player) {
        this.player.play().catch(() => {});
      }
    },
  },
  mounted() {
    if (window.innerWidth <= 600) {
      this.playerOptions.controls = [
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "settings",
      ];
    }
    if (this.$refs.plyr?.player) {
      this.player = this.$refs.plyr.player;
      this.player.on("play", () => this.$emit("podcast-play"));
      this.player.on("ended", this.onEnded);
    }
  },
  methods: {
    onPlay() {
      this.$emit("podcast-play");
    },
    onEnded() {
      this.$emit("ended");
    },
    onCanPlay() {
      if (this.player?.paused) {
        this.player.play().catch(() => {});
      }
    },
    playNext() {
      if (this.hasNext) this.$emit("play-next");
    },
    playPrev() {
      if (this.hasPrev) this.$emit("play-prev");
    },
    pause() {
      this.player?.pause();
    },
  },
};
</script>

<style scoped>
.podcast-player-wrapper {
  max-width: 100vw;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 8px;
  background: #fff;
  z-index: 9999;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  overflow: visible;
}
.close-btn {
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10001;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s;
}
.close-btn:hover {
  background: #eee;
}
.playlist-title {
  text-align: center;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0.5rem 0 0.2rem 0;
  color: #181818;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.player-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}
.playlist-btn {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0;
}
.playlist-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.playlist-btn svg {
  width: 36px;
  height: 36px;
  fill: #181818;
}
.vue-plyr {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  min-width: 0;
}
::v-deep .plyr__controls {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
  width: 100% !important;
  gap: 6px;
}
::v-deep .plyr__control,
::v-deep .plyr__time,
::v-deep .plyr__progress {
  margin: 0 4px !important;
}
::v-deep .plyr__progress {
  flex: 1 1 auto !important;
  max-width: 60vw !important;
  height: 6px !important;
  border-radius: 5px !important;
}
::v-deep .plyr__progress__buffer {
  display: none !important;
}
::v-deep .plyr__progress__played {
  height: 6px !important;
  border-radius: 5px !important;
}
::v-deep .plyr__time {
  width: 48px !important;
  text-align: center;
  font-size: 12px !important;
  flex-shrink: 0;
}
::v-deep .plyr__menu__container {
  bottom: 40px !important;
  top: auto !important;
  left: 50% !important;
  right: auto !important;
  transform: translateX(-50%) !important;
  max-height: 250px !important;
  overflow-y: auto !important;
  z-index: 10000 !important;
  box-shadow: 0 2px 12px rgba(0,0,0,0.12);
}

@media (max-width: 600px) {
  .player-row {
    padding: 0 4px;
    gap: 6px;
    flex-wrap: nowrap;
  }

  .playlist-btn {
    width: 32px;
    height: 32px;
  }

  .vue-plyr {
    padding: 0 4px;
  }

  ::v-deep .plyr__controls {
    flex-wrap: nowrap !important;
    justify-content: center !important;
    overflow-x: auto !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  ::v-deep .plyr__controls::-webkit-scrollbar {
    display: none;
  }

  ::v-deep .plyr__time {
    font-size: 11px !important;
    width: 40px !important;
  }

  ::v-deep .plyr__progress {
    flex: 1 1 auto !important;
    max-width: none !important;
  }

  ::v-deep .plyr__volume {
    min-width: 32px !important;
    max-width: 40px !important;
    overflow: hidden;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  ::v-deep .plyr__volume input[type="range"] {
    display: none !important;
  }

  ::v-deep .plyr__menu__container {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    width: 100vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
    bottom: 56px !important;
    top: auto !important;
    border-radius: 0 0 8px 8px !important;
    z-index: 20000 !important;
    margin: 0 !important;
    transform: none !important;
    box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
    background: #fff !important;
  }
}
</style>
